/**
 * Texto de "aprovar todas" — decidir N propostas do Modo Agente num gesto só.
 *
 * Pedido do owner (16/09/2026): com 3 propostas abertas ele quer decidir de
 * uma vez, "dando escala". Arquivo PRÓPRIO (mesmo padrão de
 * `Text.escopo-cliente.js`/`Text.memoria.js`) — `TextChat.js` está em edição
 * por outra frente nesta mesma rodada.
 *
 * Consumidores: `views/chat/view-chat-lote.mixin.js` (modal de confirmação e
 * os três snacks de desfecho) e `components/run-status-chip/` (rótulo do
 * botão no chip do cabeçalho).
 *
 * ⚠️ A copy aqui existe para nunca prometer atomicidade: **não há rota de
 * lote**, são N POSTs independentes, então metade pode gravar. Um texto do
 * tipo "aprovando tudo" faria o consultor ler falha parcial como falha total
 * — e reenviar decisões que já estão no banco.
 */
const plural = (n, um, varios) => (n === 1 ? um : varios)

export const PROPOSTAS_LOTE_TEXT = {
  // Rótulo do botão no chip. Traz o número porque o chip já diz "(3 propostas
  // abertas)" ao lado: o botão sem número pareceria uma ação sobre a última.
  botao: (n) => 'Aprovar ' + n + ' ' + plural(n, 'proposta', 'propostas'),
  ariaBotao: (n) => 'Aprovar ' + n + ' ' + plural(n, 'proposta aberta', 'propostas abertas'),
  titulo: (n) => 'Aprovar ' + n + ' ' + plural(n, 'proposta', 'propostas'),
  /**
   * O corpo LISTA o que vai ser gravado, não só a contagem. Aprovar aqui é
   * autorizar N escritas irreversíveis (criar tarefa, atualizar cliente): o
   * consultor precisa reconhecer cada uma antes, e o número sozinho não
   * permite isso.
   *
   * A última linha é a promessa honesta: cada proposta é gravada por conta
   * própria, e a falha de uma não desfaz as outras.
   */
  corpo: (resumos) => [
    'Estas ' + plural(resumos.length, 'operação será gravada', 'operações serão gravadas') + ' agora:',
    '',
    resumos.map((r) => '• ' + r).join('\n'),
    '',
    'Cada uma é registrada separadamente: se alguma falhar, as demais continuam gravadas.',
  ].join('\n'),
  botaoConfirmar: 'Aprovar',
  botaoCancelar: 'Cancelar',
  // Desfecho: UM aviso por gesto, e ele nunca arredonda para "tudo certo".
  todasGravadas: (n) => n + ' ' + plural(n, 'proposta aprovada', 'propostas aprovadas') + '.',
  parcial: (gravadas, falhas) =>
    gravadas + ' ' + plural(gravadas, 'proposta aprovada', 'propostas aprovadas') + ', ' +
    falhas + ' ' + plural(falhas, 'não foi registrada', 'não foram registradas') +
    '. Use "Tentar novamente" no cartão de cada uma.',
  todasFalharam: (n) =>
    'Nenhuma das ' + n + ' propostas foi registrada. Tente novamente pelos cartões ou recarregue a conversa.',
  // Fallback do item da lista: proposta sem `preview` (evento antigo ou
  // reidratação parcial) ainda precisa aparecer — some da lista seria pior
  // que aparecer sem descrição, porque ela SERÁ gravada do mesmo jeito.
  itemSemResumo: (idProposta) => 'Operação ' + idProposta,
}

/**
 * [spec-003 T302 / US-8] O relatório do lote — "o que foi e o que NÃO foi
 * feito", por ação.
 *
 * ⚠️ Nenhuma frase aqui arredonda o desfecho para cima. O gestor responde pelo
 * que a IA fez: "3 aprovadas" sobre um lote com uma falha e uma expirada é a
 * mentira que ele levaria para a reunião. Por isso o resumo conta os QUATRO
 * grupos e a linha de cada ação diz recibo OU motivo — nunca nada.
 */
export const RELATORIO_DO_LOTE = Object.freeze({
  TITULO: 'O que foi feito',
  ARIA_LISTA: 'Relatório das ações decididas neste lote',
  // Rótulo por desfecho (chaves = `DESFECHO` de `utils/desfecho-da-proposta`).
  ROTULO: Object.freeze({
    executada: 'Executada',
    negada: 'Negada',
    falhou: 'Falhou',
    nao_decidida: 'Não decidida',
  }),
  // Motivo padrão de cada desfecho SEM recibo. O da falha é sobrescrito pelo
  // texto que o servidor (ou o POST) devolveu — ele é mais útil que o nosso.
  MOTIVO: Object.freeze({
    negada: 'Você negou esta ação, e nada foi gravado.',
    expirada: 'A proposta expirou sem decisão e nada foi gravado. Peça uma nova proposta para executá-la.',
    invalidada: 'Esta proposta foi substituída por outra antes de ser decidida.',
    falhou: 'Não foi possível registrar a decisão. Use "Tentar novamente" no cartão da ação.',
  }),
  // A contagem do cabeçalho: só os grupos com pelo menos uma ação aparecem.
  RESUMO: (contagens) =>
    [
      contagens.executada ? contagens.executada + ' executada' + (contagens.executada === 1 ? '' : 's') : '',
      contagens.negada ? contagens.negada + ' negada' + (contagens.negada === 1 ? '' : 's') : '',
      contagens.falhou ? contagens.falhou + ' com falha' : '',
      contagens.nao_decidida ? contagens.nao_decidida + ' sem decisão' : '',
    ]
      .filter(Boolean)
      .join(' · '),
  // [T313] Esta frase dizia "vale para a sessão atual" porque o relatório
  // MORRIA num F5. Não morre mais: `GET /conversas/{id}/run-atual` devolve a
  // trilha de `run_decisoes` (v661) e o front a reidrata. Manter o texto
  // antigo seria pior que não ter nota nenhuma — ele mandaria o gestor
  // salvar por fora um relatório que o run já guarda.
  SO_NESTA_SESSAO: 'Este resumo fica registrado no run e continua aqui se você recarregar a página.',
})
